import {readWireMinimumPrice} from "@/features/pricing";
import {ProductService} from "../../domain/product-service.entity";
import {
  BookingQuestion,
  ProductServiceCurrency,
  ProductServiceSummary,
  ProductServiceOrderSummary,
  ProductServiceWorkDelivery,
  ProductServiceSalesInsights,
  ProductServiceCustomerPurchase,
  CreateProductServiceInput,
  UpdateProductServiceInput,
} from "../../domain/product-service.types";
import {
  V2ProductServiceResponse,
  V2ProductServiceCurrency,
  V2ProductServiceListItem,
  V2ProductServiceOrderListItem,
  V2WorkDeliveryItem,
  V2SalesInsightsResponse,
  V2CustomerPurchaseItem,
  V2BookingQuestion,
  V2CreateProductServiceRequest,
  V2UpdateProductServiceRequest,
} from "./product-service.mapper.types";

/**
 * Maps V2 API responses to ProductService domain entities.
 *
 * Pure TypeScript - no React imports.
 */
export const ProductServiceMapperV2 = {
  /**
   * Maps a single V2 API response to a ProductService entity.
   */
  toDomain(response: V2ProductServiceResponse): ProductService {
    return ProductService.create({
      id: response.id,
      userId: response.userId,
      name: response.name,
      slug: response.slug,
      tinyUrl: response.tinyUrl,
      description: response.description,
      deliveryTimeframeUnit: response.deliveryTimeframeUnit,
      deliveryTimeframeValue: response.deliveryTimeframeValue,
      hideFromProfile: response.hideFromProfile,
      urlRedirect: response.urlRedirect,
      redirectType: response.redirectType,
      telegramGroupChatId: response.telegramGroupChatId,
      statusId: response.statusId,
      requestPhoneNumber: response.requestPhoneNumber,
      purchaseLimit: response.purchaseLimit,
      bookingQuestions: this.mapBookingQuestions(response.bookingQuestions),
      coverImages: response.coverImages ?? [],
      callPrice: response.callPrice,
      callPriceCurrency: response.callPriceCurrency ?? null,
      enableCurrencyConversion: response.enableCurrencyConversion,
      currencies: this.mapCurrencies(response.currencies),
      isPayWhatYouWant: response.isPayWhatYouWant ?? false,
      createdAt: response.createdAt,
      updatedAt: response.updatedAt,
    });
  },

  /**
   * Maps an array of full V2 API responses to ProductService entities.
   * NOTE: only valid for full (single-item) responses — the list endpoint
   * returns thin items; use `toSummaryList` for those.
   */
  toDomainList(responses: V2ProductServiceResponse[]): ProductService[] {
    return responses.map((response) => this.toDomain(response));
  },

  /**
   * Maps a thin V2 list item to a ProductServiceSummary (mostly passthrough —
   * v2 already uses canonical naming).
   */
  toSummary(item: V2ProductServiceListItem): ProductServiceSummary {
    return {
      id: item.id,
      name: item.name,
      tinyUrl: item.tinyUrl,
      imageUrl: item.imageUrl ?? null,
      currency: item.currency,
      amount: item.amount,
      ordersInProgress: item.ordersInProgress,
      ordersCompleted: item.ordersCompleted,
      totalRevenue: item.totalRevenue,
    };
  },

  /**
   * Maps an array of thin V2 list items to ProductServiceSummary[].
   */
  toSummaryList(items: V2ProductServiceListItem[]): ProductServiceSummary[] {
    return items.map((item) => this.toSummary(item));
  },

  /**
   * Maps a thin V2 order list item to a ProductServiceOrderSummary (passthrough).
   */
  toOrderSummary(item: V2ProductServiceOrderListItem): ProductServiceOrderSummary {
    return {
      id: item.id,
      productServiceName: item.productServiceName,
      firstName: item.firstName,
      lastName: item.lastName,
      emailAddress: item.emailAddress ?? "",
      currency: item.currency,
      amount: item.amount,
      statusId: item.statusId,
      statusName: item.statusName,
      deliveryDate: item.deliveryDate ?? null,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
    };
  },

  toOrderSummaryList(
    items: V2ProductServiceOrderListItem[]
  ): ProductServiceOrderSummary[] {
    return items.map((item) => this.toOrderSummary(item));
  },

  /**
   * Maps a V2 work-delivery item to the canonical shape (passthrough).
   */
  toWorkDelivery(item: V2WorkDeliveryItem): ProductServiceWorkDelivery {
    return {
      id: item.id,
      statusId: item.statusId,
      statusName: item.statusName,
      deliveryDate: item.deliveryDate ?? null,
      delivery: item.delivery ?? [],
      customerComment: item.customerComment ?? null,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
    };
  },

  toWorkDeliveryList(items: V2WorkDeliveryItem[]): ProductServiceWorkDelivery[] {
    return (items ?? []).map((item) => this.toWorkDelivery(item));
  },

  /**
   * Maps the V2 sales-insights response to the canonical shape.
   * V2's currency-keyed revenue map is flattened to an array.
   */
  toSalesInsights(response: V2SalesInsightsResponse): ProductServiceSalesInsights {
    return {
      totalRevenue: Object.entries(response.totalRevenueByCurrency ?? {}).map(
        ([currency, totalRevenue]) => ({currency, totalRevenue})
      ),
      totalPurchases: response.totalSales,
      pendingOrders: response.pendingOrders,
      completedOrders: response.completedOrders,
    };
  },

  /**
   * Maps a V2 customer-purchase item to the canonical shape.
   */
  toCustomerPurchase(item: V2CustomerPurchaseItem): ProductServiceCustomerPurchase {
    return {
      id: item.id,
      userId: item.userId,
      productServiceId: item.productServiceId,
      firstName: item.firstName,
      lastName: item.lastName,
      emailAddress: item.emailAddress,
      currency: item.currency,
      amount: item.amount,
      charges: item.charges,
      reference: item.reference,
      statusId: item.statusId,
      statusName: item.statusName,
      isPaid: item.isPaid,
      paymentChannel: item.paymentChannel ?? null,
      discountCode: item.discountCode ?? null,
      chargeBearer: item.chargeBearer ?? null,
      bookingQuestions: item.bookingQuestions ?? [],
      deliveryDate: item.deliveryDate ?? null,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
    };
  },

  toCustomerPurchaseList(
    items: V2CustomerPurchaseItem[]
  ): ProductServiceCustomerPurchase[] {
    return items.map((item) => this.toCustomerPurchase(item));
  },

  /**
   * Maps a canonical create input to the V2 (camelCase) request payload.
   * Currencies keep the v1 `{callPriceCurrency, callPrice}` shape, plus `minCallPrice`
   * under Pay What You Want. Booking questions use snake_case `answer_type`.
   *
   * Product Services put EVERY currency row into `currencies[]`, base row included —
   * unlike live events and booking services, which split the base row into top-level
   * scalars. See `docs/adr/0002-pricing-slice-uniform-currency-rows.md`.
   */
  toCreateRequest(input: CreateProductServiceInput): V2CreateProductServiceRequest {
    return {
      name: input.name,
      description: input.description,
      deliveryTimeframeUnit: input.deliveryTimeframeUnit,
      deliveryTimeframeValue: input.deliveryTimeframeValue,
      urlRedirect: input.urlRedirect,
      // The API's enum is `url | telegram`, so `none` must not reach it. Omitting the key
      // is how no redirect is said, on create and update alike.
      ...(input.redirectType === "url" || input.redirectType === "telegram"
        ? {redirectType: input.redirectType}
        : {}),
      telegramGroupChatId: input.telegramGroupChatId ?? null,
      hideFromProfile: input.hideFromProfile,
      requestPhoneNumber: input.requestPhoneNumber,
      // Omitted outright when the coach set no cap — see `toPurchaseLimitPayload`.
      ...(input.purchaseLimit === undefined ? {} : {purchaseLimit: input.purchaseLimit}),
      currencies: input.currencies.map((c) => ({
        callPriceCurrency: c.callPriceCurrency,
        callPrice: c.callPrice,
        // Only Pay What You Want has a Minimum Price; omit rather than send a zero the
        // other two models would have to mean something by.
        ...(c.minCallPrice === undefined ? {} : {minCallPrice: c.minCallPrice}),
      })),
      isPayWhatYouWant: input.isPayWhatYouWant,
      bookingQuestions: input.bookingQuestions.map((q) => ({
        question: q.question,
        required: q.required,
        answer_type: q.answerType,
        // The API rejects an empty options list, so send it only when there is one.
        ...(q.options && q.options.length > 0 ? {options: q.options} : {}),
      })),
      enableCurrencyConversion: input.enableCurrencyConversion,
      selectedCurrencies: input.selectedCurrencies,
    };
  },

  /**
   * Maps a canonical update input to the V2 request payload
   * (create request + serviceId in the body).
   */
  toUpdateRequest(input: UpdateProductServiceInput): V2UpdateProductServiceRequest {
    return {
      ...this.toCreateRequest(input),
      serviceId: input.serviceId,
    };
  },

  /**
   * Maps V2 currencies array to domain format.
   * V2 uses camelCase, so mostly a passthrough — the Minimum Price is the exception.
   *
   * The wire spells it `minAmount` and carries it as a Decimal string; the domain spells
   * it `minCallPrice` and holds a number. That is the whole of the rename, and it goes no
   * further than this function.
   */
  mapCurrencies(currencies?: V2ProductServiceCurrency[]): ProductServiceCurrency[] {
    if (!currencies) {
      return [];
    }

    return currencies.map((c) => ({
      id: c.id,
      userId: c.userId,
      serviceId: c.serviceId,
      serviceType: c.serviceType,
      currency: c.currency,
      amount: c.amount,
      minCallPrice: readWireMinimumPrice(c.minAmount),
      isAutoConverted: c.isAutoConverted,
      isBase: c.isBase,
      createdAt: c.createdAt,
      updatedAt: c.updatedAt,
    }));
  },

  /**
   * Maps V2 booking questions to domain format.
   * The API stores `answer_type`, but questions saved before it enforced that key still
   * come back as camelCase `answerType`, so read both.
   */
  mapBookingQuestions(questions?: V2BookingQuestion[] | null): BookingQuestion[] {
    if (!questions) {
      return [];
    }

    return questions.map((q) => ({
      question: q.question,
      answerType: (q.answer_type ??
        q.answerType ??
        "short-text") as BookingQuestion["answerType"],
      required: q.required,
      options: q.options,
    }));
  },
};
